import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { BLOCKS, type Document } from '@contentful/rich-text-types'
import { type IOptInType } from '@pcln/contentful-components'
import { AcquisitionForm, type AcquisitionFormData, HorizonContentfulRichText, Module } from '@pcln/horizon'
import { useState } from 'react'

import useContentfulVariation from '@/hooks/useContentfulVariation'
import { hasContentTypeId } from '@/shared-utils/content-model-type-guards'

import CONTENT from './content'
import type { SourceId } from './types'
import submitEmail from './utils'

type EmailSignup = {
  appCode: 'MOBILEWEB' | 'DESKTOP'
  bfcmEmailOptIn?: boolean
  sourceId: SourceId
}

function isIOptInType(content: unknown): content is IOptInType {
  return hasContentTypeId(content) && content.contentTypeId === 'optIn'
}

function mapContentfulContent(optIn: IOptInType) {
  const { label } = optIn.callToAction ?? {}
  const { placeholder, error } = optIn?.input?.[0] ?? {}
  const {
    headline = CONTENT.headline,
    subheadline = CONTENT.subheadline,
    successHeadline = CONTENT.successHeadline,
  } = optIn

  return {
    headline,
    subheadline,
    successHeadline,
    placeholder: placeholder ?? CONTENT.placeholder,
    invalidEmailError: error ?? CONTENT.invalidEmailError.message,
    buttonText: label ?? CONTENT.buttonText,
  }
}

function combineDocuments(doc1: Document, doc2: Document): Document {
  return {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: [...doc1.content, ...doc2.content],
  }
}

export default function EmailSignUp({ appCode, bfcmEmailOptIn = false, sourceId }: Readonly<EmailSignup>) {
  const optIn = useContentfulVariation('emailOptIn', isIOptInType)
  const { headline, subheadline, successHeadline, placeholder, invalidEmailError, buttonText } = optIn
    ? mapContentfulContent(optIn)
    : { ...CONTENT, invalidEmailError: CONTENT.invalidEmailError.message }

  const [signUpState, setSignUpState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const onSubmit = async ({ email }: AcquisitionFormData) => {
    if (!email) return
    try {
      setSignUpState('loading')
      await submitEmail(email, sourceId, appCode)
      setSignUpState('success')
    } catch {
      setSignUpState('error')
    }
  }

  const richTextDocument = combineDocuments(headline, subheadline)
  return (
    <Module.Subscribe
      display='simple'
      emphasis={bfcmEmailOptIn ? 'bold' : 'regular'}
      panel={bfcmEmailOptIn}
      hasContentfulRichText={true}
      formProps={{ type: 'email', onSubmit }}
    >
      <div className='flex w-full flex-col gap-4 @2xl:flex-row @2xl:items-center @2xl:gap-16'>
        <HorizonContentfulRichText
          className='w-full @2xl:w-1/2 @2xl:shrink-0'
          document={richTextDocument}
          variant='marketing'
          emphasis={bfcmEmailOptIn ? 'bold' : 'regular'}
        />
        <div className='w-full @2xl:w-1/2'>
          <AcquisitionForm
            type='email'
            emphasis={bfcmEmailOptIn ? 'bold' : 'regular'}
            onSubmit={onSubmit}
            buttonText={buttonText}
            emailPlaceholder={placeholder}
            isLoading={signUpState === 'loading'}
            successMessage={
              signUpState === 'success' ? documentToPlainTextString(successHeadline) : undefined
            }
            errorMessage={signUpState === 'error' ? invalidEmailError : undefined}
          />
        </div>
      </div>
    </Module.Subscribe>
  )
}
